import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { withSize } from 'react-sizeme';

import {
    Sizes,
    Colours,
    Typography,
    StarReviews,
    CaptionedIcon,
    ImageSizesByType,
    createRibbonMixin,
} from '@ratehub/base-ui';
import {
    CreditCardCTA,
    CreditCardShape,
    getCardRibbonMessage,
    getGiftOfferSummary,
    useApplyForCreditCardCallback,
} from '@ratehub/cc-common';

import { CARD_IMAGE_WIDTH, HORIZONTAL_WHITE_SPACE } from '../definitions/Layout';


function HeaderBlock({
    cardInfo,
    onReviewsClick,

    size,
    ...otherProps
}) {
    const intl = useIntl();
    const applyForCreditCardCallback = useApplyForCreditCardCallback();

    // on first render size.width is undefined, forcing isCompact to false ==> desktop layout
    const isCompact = size?.width < MAX_MOBILE_WIDTH;

    const {
        id,
        name,
        cardImageURL,
        reviewRating,
        reviewCount,
        giftCardOffer,
        cardTypes,
        isMonetized,
        isHidden,
    } = cardInfo;

    const handleCCCallback = isMonetized && !isHidden
        ? () => applyForCreditCardCallback({ cardInfo })
        : undefined;


    return (
        <MyFlexLayout
            {...otherProps}
            isCompact={isCompact}
            data-ribbon-message={getCardRibbonMessage({ cardInfo, intl })}
        >
            <img
                className="image"
                src={cardImageURL}
                alt=""
                width={ImageSizesByType.CREDIT_CARDS.CARD.WIDTH}
                height={ImageSizesByType.CREDIT_CARDS.CARD.HEIGHT}
            />

            <div
                className="titleArea"
            >
                <StarReviews
                    title={name}
                    size="huge"
                    onTitleClick={handleCCCallback}

                    ratingPercent={reviewRating || 0}
                    reviewCount={reviewCount}
                    onReviewsClick={onReviewsClick}
                    forceStars={true}
                    forceReviewText={true}

                    alignment={isCompact ? 'center' : 'left'}
                />

                <If condition={giftCardOffer}>
                    <CaptionedIcon
                        className="giftCardBadge"
                        iconKey="gift"
                        iconProps={{ stroke: Colours.LIME }}
                        message={getGiftOfferSummary({ giftCardOffer, intl })}
                        weight={Typography.WEIGHTS.REGULAR}
                        size="medium"
                        alignment={isCompact ? 'centre' : 'left'}
                    />
                </If>
            </div>

            <If condition={handleCCCallback}>
                <div className="ctaButton">
                    <CreditCardCTA
                        cardTypes={cardTypes}
                        giftCardOffer={giftCardOffer}
                        cardId={id}

                        onClick={handleCCCallback}
                        buttonSize="large"
                    />
                </div>
            </If>
        </MyFlexLayout>
    );
}


HeaderBlock.propTypes = {
    cardInfo: CreditCardShape.isRequired,
    onReviewsClick: PropTypes.func,

    size: PropTypes.shape({
        width: PropTypes.number,    // with noPlaceholder true, this will be undefined (once); avoid warning
    }).isRequired,
};


const MAX_MOBILE_WIDTH = 650;    // ~47em, need px for withSize()
const VERTICAL_GAP = Sizes.SPACING.TWO;

const MyFlexLayout = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    padding: ${Sizes.SPACING.THREE} ${HORIZONTAL_WHITE_SPACE} ${Sizes.SPACING.ONE} ${HORIZONTAL_WHITE_SPACE};
    margin-bottom: ${Sizes.SPACING.TWO};
    background-color: ${Colours.STONE_LIGHT};

    &[data-ribbon-message] {
        ${createRibbonMixin('12px', undefined, false)};  // false means do not transform text

        &::before {
            content: attr(data-ribbon-message);
        }
    }

    > .image {
        height: auto;
        width: ${CARD_IMAGE_WIDTH};
        margin-right: ${HORIZONTAL_WHITE_SPACE};
        margin-bottom: ${VERTICAL_GAP};

        ${({ isCompact }) => isCompact && `
            margin-right: auto;
            margin-left: auto;
        `};
    }

    > .titleArea {
        flex: 280px 1 1;
        margin-right: auto;
        margin-bottom: ${VERTICAL_GAP};
        padding-right: ${Sizes.SPACING.TWO};

        ${({ isCompact }) => isCompact && `
            flex-basis: 100%;
            margin-right: 0;
            padding-right: 0;
        `};

        > .giftCardBadge {
            margin-top: ${Sizes.SPACING.HALF};
            min-width: 335px;

            ${({ isCompact }) => isCompact && `
                min-width: auto;
            `};
        }
    }

    > .ctaButton {
        flex: ${CARD_IMAGE_WIDTH} 0 0;
        margin-bottom: ${VERTICAL_GAP};

        ${({ isCompact }) => isCompact && `
            margin-right: auto;
            margin-left: auto;
        `}
    }
`;

// noPlaceholder allows server-side rendering
export default withSize({ noPlaceholder: true })(observer(HeaderBlock));
